import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    TextField,
    Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { LinearProgress } from "@mui/material";
import ClearIcon from '@mui/icons-material/Clear';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuditsController } from "./context/audits-controller";
import { useAuditsState } from "./context/audits-context";
import { IListAuditsRequest } from "../../model/base/request/list-audits-request";
import PageHeader from "../components/shared/page-header";
import { useNavigate } from "../../hooks/useNavigate";

export function AuditsComponent() {
    const [initialLoading, setInitialLoading] = useState(true);
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData } = useAuditsController();
    const { audits } = useAuditsState();
    const [paramsRequest, setParamsRequest] = useState<IListAuditsRequest>({ page: 0, pageSize: 10 } as IListAuditsRequest);
    const navigate = useNavigate();

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequest);
            setLoadMoreData(false);
            setInitialLoading(false);
        };
        searchData();
    }, [paramsRequest]);

    const handleClearFilters = () => {
        setParamsRequest({ page: 0, pageSize: 10 } as IListAuditsRequest);
    };

    const getStatusColor = (status: number) => {
        switch (status) {
            case 1:
                return 'success';
            case 2:
                return 'warning';
            case 3:
                return 'info';
            default:
                return 'default';
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'unitName',
            headerName: 'Unidade',
            flex: 1,
        },
        {
            field: 'processNumber',
            headerName: 'Nº Processo',
            flex: 1,
        },
        {
            field: 'year',
            headerName: 'Ano',
            width: 100,
            align: 'center',
            headerAlign: 'center',
        },
        {
            field: 'statusDescription',
            headerName: 'Situação',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Chip
                    label={params.value || 'Não especificado'}
                    color={getStatusColor(params.row.status)}
                    size="small"
                />
            ),
        },
        {
            field: 'url',
            headerName: 'Arquivo',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                params.value ? (
                    <Link
                        href={params.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                        <OpenInNewIcon fontSize="small" />
                    </Link>
                ) : (
                    <span>-</span>
                )
            ),
        },
        {
            field: 'actions',
            headerName: 'Ações',
            width: 150,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params) => (
                <Button
                    variant="text"
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate(`/auditoria/${params.row.id}`)}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500
                    }}
                >
                    Ver detalhes
                </Button>
            ),
        },
    ];

    return (
        <Box>
            <PageHeader
                title="Auditoria"
                subtitle="Acompanhe os processos de auditoria realizados no município, garantindo transparência e fiscalização dos atos públicos."
                breadcrumbs={[{ label: 'Auditoria' }]}
            />

            {initialLoading ? (
                <Skeleton variant="rounded" width="100%" height={500} sx={{ borderRadius: 2 }} />
            ) : (
                <React.Fragment>
                    <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={5}>
                                <TextField
                                    fullWidth
                                    label="Buscar"
                                    placeholder="Digite para buscar"
                                    value={paramsRequest.term || ''}
                                    onChange={(e) => {
                                        setParamsRequest({ ...paramsRequest, page: 0, term: e.target.value });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.5}>
                                <TextField
                                    fullWidth
                                    label="Ano"
                                    type="number"
                                    placeholder="Digite o ano"
                                    value={paramsRequest.year || ''}
                                    onChange={(e) => {
                                        setParamsRequest({
                                            ...paramsRequest,
                                            page: 0,
                                            year: e.target.value ? Number(e.target.value) : undefined
                                        });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.5}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={paramsRequest.statusId || ''}
                                        label="Status"
                                        onChange={(e) =>
                                            setParamsRequest({
                                                ...paramsRequest,
                                                page: 0,
                                                statusId: e.target.value as number,
                                            })
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Todos</em>
                                        </MenuItem>
                                        <MenuItem value={1}>ENTREGUE</MenuItem>
                                        <MenuItem value={2}>SOBRESTADO</MenuItem>
                                        <MenuItem value={3}>JULGADO</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    Limpar Filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, minHeight: 500 }} elevation={2}>
                        {loadMoreData ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                                <LinearProgress sx={{ width: '50%' }} />
                            </Box>
                        ) : audits?.items?.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                                <Typography variant="h6" color="text.secondary">
                                    Nenhuma auditoria encontrada
                                </Typography>
                            </Box>
                        ) : (
                            <DataGrid
                                key={JSON.stringify(paramsRequest)}
                                rows={audits?.items || []}
                                rowCount={audits?.totalRecords || 0}
                                columns={columns}
                                getRowId={(row) => row.id}
                                hideFooterSelectedRowCount
                                paginationModel={{ page: paramsRequest.page, pageSize: paramsRequest.pageSize } as GridPaginationModel}
                                onPaginationModelChange={(e) => { setParamsRequest({ ...paramsRequest, page: e.page, pageSize: e.pageSize }) }}
                                pageSizeOptions={[5]}
                                paginationMode="server"
                                slots={{
                                    loadingOverlay: LinearProgress,
                                }}
                                loading={loadMoreData}
                                localeText={{
                                    MuiTablePagination: {
                                        labelDisplayedRows: ({ from, to, count }) =>
                                            `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`,
                                        labelRowsPerPage: 'Linhas por página:',
                                    },
                                    noRowsLabel: 'Nenhum registro encontrado',
                                    noResultsOverlayLabel: 'Nenhum resultado encontrado',
                                }}
                            />
                        )}
                    </Paper>
                </React.Fragment>
            )}
        </Box>
    );
}
